/**
 * MODULE: platform/browser/frontend/components/BrowserAssistantIdleLanding.tsx
 * PURPOSE: Idle browser chrome — matches AssistantPageLanding layout and ASSISTANT_PAGE_BG colors.
 */

import { GlobeAltIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { ASSISTANT_RAIL_BG_CLASS } from '@design-system/components/AiAssistant/assistantPageTheme';

export type BrowserAssistantIdleLandingProps = {
  compact?: boolean;
  providerLabel?: string;
  providerReady?: boolean;
  /** Mission picker, reconnect actions, etc. */
  children?: ReactNode;
  /** Composer / dock region below the centered block (panel only). */
  footer?: ReactNode;
  className?: string;
  testId?: string;
};

export function BrowserAssistantIdleLanding({
  compact = false,
  providerLabel,
  providerReady = false,
  children,
  footer,
  className = '',
  testId = 'browser-idle-landing',
}: BrowserAssistantIdleLandingProps) {
  return (
    <div
      data-testid={testId}
      className={`flex min-h-0 w-full flex-col ${ASSISTANT_RAIL_BG_CLASS} ${className}`.trim()}
    >
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-6 text-center">
        <GlobeAltIcon
          className={`shrink-0 text-gray-400 dark:text-gray-500 ${compact ? 'h-10 w-10' : 'h-14 w-14'}`}
          aria-hidden
        />
        <h1
          className={`mt-3 font-normal tracking-tight text-gray-700 dark:text-gray-200 ${
            compact ? 'text-xl' : 'text-2xl'
          }`}
        >
          AI Browser
        </h1>
        {providerLabel && providerReady ? (
          <p
            className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-400"
            data-testid="browser-provider-ready"
          >
            {providerLabel} ready — low-latency local Chromium
          </p>
        ) : null}
        <p className="mt-2 max-w-md text-sm text-gray-600 dark:text-gray-400">
          Pick a mission or describe a task — the agent drives a remote Chromium session for you.
        </p>
        {children ? <div className="mt-4 w-full max-w-lg">{children}</div> : null}
      </div>
      {footer ? <div className="shrink-0">{footer}</div> : null}
    </div>
  );
}
